// services/astro-bot-miniapp/src/screens/onboarding/InfoReveal.tsx
// OB5alphaInfo — InfoReveal: kicker + NatalWheel + Sun/Moon/Rising/Dominant table + Archetype card.
// All data comes from GET /api/v1/me/chart (archetype + dominant computed server-side).

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CosmosBackdrop } from '../../components/brand/CosmosBackdrop.js';
import { AspectsTable } from '../../components/onboarding/AspectsTable.js';
import { ArchetypeCard } from '../../components/onboarding/ArchetypeCard.js';
import { NatalWheel } from '../../components/onboarding/NatalWheel.js';
import { useChart } from '../../hooks/useChart.js';
import { useDailyBrief } from '../../hooks/useDailyBrief.js';
import { apiFetch } from '../../lib/api-client.js';
import { ONBOARDING_STEPS } from '../../lib/onboarding-steps.js';
import { useAuth } from '../../store/auth.js';
import styles from './InfoReveal.module.css';

export function InfoReveal(): JSX.Element {
  const { t } = useTranslation('flows');
  const { t: tc } = useTranslation('common');
  const nav = useNavigate();
  const jwt = useAuth(s => s.jwt);
  const { data: chart } = useChart();
  // Warm today's brief now (chart is ready here) so PushConsent renders it instantly.
  useDailyBrief();

  const ready = chart?.status === 'ready' ? chart : null;
  const sun = ready?.sun;
  const moon = ready?.moon;
  const asc = ready?.ascendant;
  const dominantPlanet = ready?.dominant?.planet ?? null;
  const dominantLabel = dominantPlanet ? tc(`planets.${dominantPlanet}`, { defaultValue: dominantPlanet }) : undefined;
  // Black Moon has no flat field on /me/chart — it lives in the points array.
  const lilith = ready?.planets?.find((p) => p.id === 'lilith')?.sign ?? null;

  // The avatar pick is no longer part of onboarding: the chart goes straight to the push
  // question. Advance the step first so a refresh there does not bounce back here.
  const next = async (): Promise<void> => {
    if (jwt) {
      try {
        await apiFetch('/api/v1/me', jwt, {
          method: 'PATCH',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ onboardingStep: ONBOARDING_STEPS.PUSH_CONSENT_PENDING }),
        });
      } catch {
        // Best-effort — PushConsent finishes onboarding (step 99) either way.
      }
    }
    nav('/onboarding/push', { replace: true });
  };

  const archetypeCode = ready?.archetype?.code;
  const archetypeName = archetypeCode
    ? t(`onboarding.reveal.archetype.codes.${archetypeCode}.label`, { defaultValue: ready?.archetype?.label ?? '' })
    : '';
  const archetypeDescription = archetypeCode
    ? t(`onboarding.reveal.archetype.codes.${archetypeCode}.description`, { defaultValue: '' })
    : '';
  const archetypeEmoji = archetypeCode
    ? t(`onboarding.reveal.archetype.codes.${archetypeCode}.emoji`, { defaultValue: '✦' })
    : '✦';
  const archetypeExpanded = archetypeCode
    ? t(`onboarding.reveal.archetype.codes.${archetypeCode}.expanded`, { defaultValue: '' })
    : '';

  return (
    <div className={styles.screen}>
      <div className={styles.backdrop}>
        <CosmosBackdrop />
      </div>

      <div className={styles.content}>
        <p className={styles.kicker}>{t('onboarding.reveal.kicker')}</p>

        {ready && (
          <NatalWheel
            planets={ready.planets}
            houses={ready.houses}
            angles={ready.angles}
            ascendantSign={ready.ascendant}
            dominantPlanet={dominantPlanet}
          />
        )}

        <AspectsTable sun={sun} moon={moon} rising={asc} dominant={dominantLabel} lilith={lilith} />

        {archetypeCode && (
          <ArchetypeCard
            name={archetypeName}
            description={archetypeDescription}
            expandedDescription={archetypeExpanded}
            emoji={archetypeEmoji}
          />
        )}

        <button
          type="button"
          className={styles.cta}
          onClick={() => { void next(); }}
        >
          {t('onboarding.reveal.cta')}
        </button>
      </div>
    </div>
  );
}
